// src/pages/FullIntakeForm.jsx
import React, { useState, useRef, useEffect } from "react";
import { AlertCircle, ArrowRight, ArrowLeft, CheckCircle, Upload } from "lucide-react";
import questionsData from "../pages/IntakeQuestionsFull.json"; // long-form JSON (same as your existing file)
import { startIntake, saveAnswer as apiSaveAnswer, submitIntake } from "../api/intakeApi"; // adjust path if needed

export default function FullIntakeForm() {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [showRedFlag, setShowRedFlag] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [formData, setFormData] = useState({});
    const [intakeUid, setIntakeUid] = useState(null);
    const [hasStarted, setHasStarted] = useState(false);
    const [showDiagnostics, setShowDiagnostics] = useState(false);

    const diagnosticsRef = useRef(null);

    const questionCardRef = useRef(null);

    const questions = questionsData;
    const currentQ = questions[currentQuestion];
    const totalQuestions = questions.length;
    const progress = ((currentQuestion + 1) / totalQuestions) * 100;

    // Sections (normalized to match Quick UI labels)
    const normalize = (s) =>
        s === "Red-Flag Screening" ? "Red-Flag Check"
            : s === "Preferences" ? "Your Preferences"
                : s === "Patient Basics" ? "About You"
                    : s;

    const sections = Array.from(new Set(questions.map(q => normalize(q.section))));
    const currentSectionIndex = sections.indexOf(normalize(currentQ.section));

    // Auto-scroll the card after Q3
    useEffect(() => {
        if (currentQuestion >= 3 && questionCardRef.current) {
            questionCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [currentQuestion]);
    
    useEffect(() => {
        if (submitted) {
            setShowDiagnostics(true); // 👈 make visible
            const timer = setTimeout(() => {
                if (diagnosticsRef.current) {
                    diagnosticsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [submitted]);



    // --- Red-flag helper ---
    const redFlagFields = new Set(questions.filter(q => q.isRedFlag).map(q => q.field));
    const updateRedFlagState = (updated) => {
        const hasRed = Array.from(redFlagFields).some(f => updated[f] === "Yes");
        setShowRedFlag(hasRed);
    };

    // --- Change handlers ---
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Progressive save for single value fields AFTER we have uid
        if (intakeUid && currentQ?.field === field && currentQ?.type !== "checkbox") {
            console.log("[FULL] saveAnswer (onChange):", { uid: intakeUid, question_key: field, answer: value });
            apiSaveAnswer({ uid: intakeUid, question_key: field, answer: value })
                .then((res) => console.log("[FULL] saveAnswer success:", res))
                .catch((e) => console.error("[FULL] saveAnswer error:", e));
        }
    };

    const handleCheckboxChange = (field, value) => {
        setFormData(prev => {
            const current = prev[field] || [];
            return {
                ...prev,
                [field]: current.includes(value)
                    ? current.filter(v => v !== value)
                    : [...current, value]
            };
        });
    };

    const handleRedFlagChange = (field, value) => {
        const updated = { ...formData, [field]: value };
        setFormData(updated);
        updateRedFlagState(updated);

        if (intakeUid) {
            console.log("[FULL] saveAnswer (redflag):", { uid: intakeUid, question_key: field, answer: value });
            apiSaveAnswer({ uid: intakeUid, question_key: field, answer: value })
                .then((res) => console.log("[FULL] saveAnswer success:", res))
                .catch((e) => console.error("[FULL] saveAnswer error:", e));
        }
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!intakeUid) return alert("Please complete the form before uploading files.");

        for (const file of files) {
            try {
                const formData = new FormData();
                formData.append("uid", intakeUid);
                formData.append("question_key", "diagnosticUploads");
                formData.append("file", file);

                console.log("[FULL] Uploading diagnostic file:", file.name);
                await apiSaveAnswer(formData, true); // 👈 we’ll handle FormData in api helper
                console.log("[FULL] Uploaded:", file.name);
            } catch (err) {
                console.error("[FULL] Upload failed:", file.name, err);
                alert(`Failed to upload ${file.name}`);
            }
        }

        setUploadedFiles((prev) => [...prev, ...files]);
    };


    // --- Validation per-question ---
    const validateQuestion = () => {
        const { field, type, otherField } = currentQ || {};
        if (!field) return true;
        if (type === "upload") return true;
        if (type === "checkbox") {
            const selected = formData[field]?.length || 0;
            if (!selected) return false;
            if (otherField && formData[field].includes("Other")) {
                return !!(formData[otherField]?.trim()?.length);
            }
            return true;
        }
        if (type === "slider" || type === "number") {
            return formData[field] !== undefined && formData[field] !== null && `${formData[field]}` !== "";
        }
        return !!(formData[field] && `${formData[field]}`.trim() !== "");
    };

    // --- Start only when user clicks NEXT on the City question ---
    const startIfCityConfirmed = async () => {
        if (hasStarted || intakeUid) return intakeUid;

        const name = (formData.fullName || "").trim();
        const phone = (formData.phone || "").trim();
        const city = (formData.city || "").trim();

        if (!(name && city && phone && phone.length === 10)) {
            console.warn("[FULL] Not starting intake: missing name/phone(10)/city", { name, phone, city });
            return null;
        }

        const payload = {
            phone_number: `+91${phone}`,
            form_type: "full", // <- Full form
            name,
            city,
        };

        console.log("[FULL] startIntake (on NEXT of City):", payload);
        try {
            const res = await startIntake(payload);
            console.log("[FULL] startIntake success:", res);

            // Your PK is 'uid' (not 'id')
            const uid =
                res?.data?.uid ??
                res?.uid ??
                res?.data?.id ??
                res?.id ??
                null;

            if (!uid) {
                console.error("[FULL] startIntake returned no uid. Response shape mismatch.", res);
                return null;
            }

            setIntakeUid(uid);
            setHasStarted(true);
            return uid;
        } catch (e) {
            console.error("[FULL] startIntake error:", e);
            return null;
        }
    };

    // --- Next/Submit flow ---
    const goNext = async () => {
        if (!validateQuestion()) return;

        // If we're on City and haven't started yet → call /start now
        if (currentQ?.field === "city" && !intakeUid) {
            const uid = await startIfCityConfirmed();
            if (!uid) return; // let user retry

            // Try to persist city (non-blocking), then advance immediately
            console.log("[FULL] saveAnswer (city after start):", { uid, question_key: "city", answer: formData.city });
            apiSaveAnswer({ uid, question_key: "city", answer: formData.city })
                .then((res) => console.log("[FULL] saveAnswer (city) success:", res))
                .catch((e) => console.error("[FULL] saveAnswer (city) error:", e));

            if (currentQuestion < totalQuestions - 1) setCurrentQuestion(prev => prev + 1);
            else handleSubmit();
            return;
        }

        // Other questions: save if we have uid
        try {
            if (intakeUid && currentQ?.field) {
                console.log("[FULL] saveAnswer (onNext):", {
                    uid: intakeUid,
                    question_key: currentQ.field,
                    answer: formData[currentQ.field],
                });
                if (intakeUid && uploadedFiles.length) {
                    for (const file of uploadedFiles) {
                        const formData = new FormData();
                        formData.append("uid", intakeUid);
                        formData.append("question_key", "diagnosticUploads");
                        formData.append("file", file);

                        console.log("[FULL] Submitting diagnostic file:", file.name);
                        await apiSaveAnswer(formData, true);
                    }
                }


                console.log("[FULL] saveAnswer (onNext) success");
            }
        } catch (e) {
            console.error("[FULL] saveAnswer (onNext) error:", e);
            // Optionally block navigation on failure:
            // return;
        }

        if (currentQuestion < totalQuestions - 1) setCurrentQuestion(prev => prev + 1);
        else handleSubmit();
    };

    const handleSubmit = async () => {
        try {
            if (intakeUid && uploadedFiles.length) {
                console.log("[FULL] saveAnswer (uploads):", {
                    uid: intakeUid,
                    question_key: "uploadedFiles",
                    answer: uploadedFiles.map(f => f.name),
                });
                await apiSaveAnswer({
                    uid: intakeUid,
                    question_key: "uploadedFiles",
                    answer: uploadedFiles.map(f => f.name),
                });
                console.log("[FULL] saveAnswer (uploads) success");
            }
            if (intakeUid) {
                console.log("[FULL] submitIntake:", { uid: intakeUid });
                await submitIntake({ uid: intakeUid });
                console.log("[FULL] submitIntake success");
            }
        } catch (err) {
            console.error("[FULL] submitIntake error:", err);
        }
        setSubmitted(true);
    };

    // --- Render ---
    if (submitted) {
        return (
            <div style={styles.container}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.logoContainer}>
                        <div style={styles.textContainer}>
                            <h1 style={styles.logoText}>Physica</h1>
                            <p style={styles.subtitle}>This intake form is co-created with experts</p>
                        </div>
                    </div>
                </div>

                {/* Thank You Section (existing) */}
                <div style={styles.thankYouCard}>
                    <CheckCircle size={64} color="#FE8235" style={{ marginBottom: 24 }} />
                    <h1 style={styles.thankYouTitle}>Thank You for Registering!</h1>
                    <p style={styles.thankYouText}>
                        Thank you for registering with Physica. A Care Manager will contact you immediately.
                    </p>
                    <div style={styles.privacyNote}>
                        <p>
                            *Your data is safe with us. We follow strict security measures to protect your privacy
                            and never share your information without consent.{" "}
                            <a href="#" style={styles.link}>Learn more</a>
                        </p>
                    </div>
                </div>

                {/* ------------------------- */}
                {/* New Section 8: Diagnostics */}
                {/* ------------------------- */}
                {showDiagnostics && (
                    <div style={styles.diagnosticsCard} ref={diagnosticsRef}>

                        <h2 style={styles.sectionTitle}>Diagnostics</h2>

                        {/* Question 1 */}
                        <h3 style={styles.questionTitle}>Have you had any scans?</h3>
                        <div style={styles.checkboxGroup}>
                            {["X-ray", "MRI", "CT", "None"].map(opt => (
                                <label
                                    key={opt}
                                    style={{
                                        ...styles.checkboxLabel,
                                        background: formData.scans?.includes(opt)
                                            ? "#FE8235"
                                            : "#FFFFFF",
                                        color: formData.scans?.includes(opt)
                                            ? "#FFFFFF"
                                            : "#4A4A4A",
                                        border: formData.scans?.includes(opt)
                                            ? "2px solid #FE8235"
                                            : "2px solid #E0E0E0",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!formData.scans?.includes(opt)}
                                        onChange={() => handleCheckboxChange("scans", opt)}
                                        style={styles.checkboxInput}
                                    />
                                    {opt}
                                </label>
                            ))}
                        </div>

                        {/* Question 2 */}
                        <h3 style={styles.questionTitle}>Upload doctor reports or prescriptions</h3>
                        <div style={styles.uploadContainer}>
                            <div style={styles.uploadArea}>
                                <Upload size={40} color="#FE8235" />
                                <p style={styles.uploadText}>Click to upload or drag and drop</p>
                                <p style={styles.uploadSubtext}>PDF, JPG, PNG (max 10MB)</p>
                                <input
                                    type="file"
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={handleFileUpload}
                                    style={styles.fileInput}
                                />
                            </div>

                            {uploadedFiles?.length > 0 && (
                                <div style={styles.fileList}>
                                    {uploadedFiles.map((file, idx) => (
                                        <div key={idx} style={styles.fileItem}>
                                            <CheckCircle size={18} color="#FE8235" />
                                            <span>{file.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={async () => {
                                try {
                                    console.log("[FULL] Submitting Diagnostics section...");
                                    if (intakeUid && uploadedFiles.length) {
                                        await apiSaveAnswer({
                                            uid: intakeUid,
                                            question_key: "diagnosticUploads",
                                            answer: uploadedFiles.map(f => f.name),
                                        });
                                        console.log("[FULL] Uploaded diagnostic files successfully");
                                    }
                                    if (intakeUid && formData.scans?.length) {
                                        await apiSaveAnswer({
                                            uid: intakeUid,
                                            question_key: "diagnosticScans",
                                            answer: formData.scans,
                                        });
                                        console.log("[FULL] Saved selected scans successfully");
                                    }

                                    alert("Diagnostics submitted successfully!");
                                    setShowDiagnostics(false); // 👈 hides the diagnostics div
                                } catch (err) {
                                    console.error("[FULL] Diagnostics upload error:", err);
                                    alert("Error uploading diagnostics, please try again.");
                                }
                            }}
                            style={{ ...styles.nextButton, marginTop: "30px" }}
                        >
                            Submit Diagnostics <CheckCircle size={20} />
                        </button>

                    </div>
                )}
            </div>
        );
    }


    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.logoContainer}>
                    <div style={styles.textContainer}>
                        <h1 style={styles.logoText}>Physica</h1>
                        <p style={styles.subtitle}>This intake form is co-created with experts</p>
                    </div>
                </div>
            </div>

            <div style={styles.mainContent}>
                {/* Back arrow (left) + centered section name (orange) */}
                <div style={styles.sectionHeader}>
                    {currentQuestion > 0 && (
                        <ArrowLeft
                            size={24}
                            color="#4A4A4A"
                            style={styles.backArrow}
                            onClick={() => setCurrentQuestion(prev => Math.max(prev - 1, 0))}
                        />
                    )}
                    <div style={styles.sectionCenter}>
                        <span style={styles.sectionName}>{sections[currentSectionIndex]}</span>
                    </div>
                </div>

                {/* Progress */}
                <div style={styles.progressContainer}>
                    <div style={styles.progressBar}>
                        <div style={{ ...styles.progressFill, width: `${progress}%` }} />
                    </div>
                    <div style={styles.progressText}>{Math.round(progress)}%</div>
                </div>

                {/* Red Flag Alert */}
                {showRedFlag && (
                    <div style={styles.redFlagAlert}>
                        <AlertCircle size={20} />
                        <div>
                            <strong>⚠️ Medical Alert</strong>
                            <p>
                                Based on your responses, immediate medical attention may be required.
                                Our Care Manager will prioritize your case.
                            </p>
                        </div>
                    </div>
                )}

                {/* Question Card */}
                <div style={styles.questionCard} ref={questionCardRef}>
                    <h2 style={styles.questionTitle}>{currentQ.question}</h2>

                    <div style={styles.answerArea}>
                        {/* TEXT */}
                        {currentQ.type === "text" && (
                            <input
                                type="text"
                                value={formData[currentQ.field] || ""}
                                onChange={(e) => handleInputChange(currentQ.field, e.target.value)}
                                style={styles.textInput}
                                placeholder={currentQ.placeholder || ""}
                            />
                        )}

                        {/* NUMBER */}
                        {currentQ.type === "number" && (
                            <input
                                type="number"
                                value={formData[currentQ.field] || ""}
                                onChange={(e) => handleInputChange(currentQ.field, e.target.value)}
                                style={styles.textInput}
                                placeholder={currentQ.placeholder || "Enter number"}
                            />
                        )}

                        {/* TEL (+91 prefix) */}
                        {currentQ.type === "tel" && (
                            <div style={styles.phoneWrapper}>
                                <span style={styles.phonePrefix}>+91</span>
                                <input
                                    type="tel"
                                    value={formData[currentQ.field] || ""}
                                    onChange={(e) => {
                                        const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                                        handleInputChange(currentQ.field, digits);
                                    }}
                                    style={styles.phoneInput}
                                    placeholder="98765 43210"
                                />
                            </div>
                        )}

                        {/* SELECT */}
                        {currentQ.type === "select" && (
                            <select
                                value={formData[currentQ.field] || ""}
                                onChange={(e) => handleInputChange(currentQ.field, e.target.value)}
                                style={styles.select}
                            >
                                <option value="">{currentQ.placeholder || "Select option"}</option>
                                {currentQ.options?.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        )}

                        {/* RADIO (auto-advance) */}
                        {currentQ.type === "radio" && (
                            <div style={styles.radioGroup}>
                                {currentQ.options?.map(option => {
                                    const isSelected = formData[currentQ.field] === option;
                                    return (
                                        <label
                                            key={option}
                                            style={{
                                                ...styles.radioLabel,
                                                background: isSelected ? "#FE8235" : "#FFFFFF",
                                                color: isSelected ? "#FFFFFF" : "#4A4A4A",
                                                border: isSelected ? "2px solid #FE8235" : "2px solid #E0E0E0",
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name={currentQ.field}
                                                value={option}
                                                checked={isSelected}
                                                onChange={(e) => {
                                                    const v = e.target.value;

                                                    if (currentQ.isRedFlag) handleRedFlagChange(currentQ.field, v);
                                                    else handleInputChange(currentQ.field, v);

                                                    // 🧠 Only auto-advance if this question does NOT require extra details
                                                    const needsDetails = currentQ.detailsField && v === "Yes";

                                                    if (!needsDetails) {
                                                        setTimeout(() => {
                                                            if (currentQuestion < totalQuestions - 1) setCurrentQuestion(prev => prev + 1);
                                                            else handleSubmit();
                                                        }, 300);
                                                    }
                                                }}

                                                style={styles.radioInput}
                                            />
                                            {option}
                                        </label>
                                    );
                                })}
                            </div>
                        )}

                        {/* CHECKBOX (+ optional "Other" field) */}
                        {currentQ.type === "checkbox" && (
                            <>
                                <div style={styles.checkboxGroup}>
                                    {currentQ.options?.map(option => {
                                        const selected = formData[currentQ.field]?.includes(option);
                                        return (
                                            <label
                                                key={option}
                                                style={{
                                                    ...styles.checkboxLabel,
                                                    background: selected ? "#FE8235" : "#FFFFFF",
                                                    color: selected ? "#FFFFFF" : "#4A4A4A",
                                                    border: selected ? "2px solid #FE8235" : "2px solid #E0E0E0",
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!selected}
                                                    onChange={() => handleCheckboxChange(currentQ.field, option)}
                                                    style={styles.checkboxInput}
                                                />
                                                {option}
                                            </label>
                                        );
                                    })}
                                </div>
                                {currentQ.otherField && (
                                    <input
                                        type="text"
                                        value={formData[currentQ.otherField] || ""}
                                        onChange={(e) => handleInputChange(currentQ.otherField, e.target.value)}
                                        style={styles.textInput}
                                        placeholder="Other (please specify)"
                                    />
                                )}
                            </>
                        )}

                        {/* SLIDER */}
                        {currentQ.type === "slider" && (
                            <div style={styles.sliderContainer}>
                                <div style={styles.sliderValue}>{formData[currentQ.field] ?? currentQ.min ?? 0}</div>
                                <input
                                    type="range"
                                    min={currentQ.min ?? 0}
                                    max={currentQ.max ?? 10}
                                    value={formData[currentQ.field] ?? currentQ.min ?? 0}
                                    onChange={(e) => handleInputChange(currentQ.field, parseInt(e.target.value, 10))}
                                    style={styles.slider}
                                />
                                <div style={styles.sliderLabels}>
                                    <span>No Pain</span>
                                    <span>Worst Pain</span>
                                </div>
                            </div>
                        )}

                        {/* TEXTAREA */}
                        {currentQ.type === "textarea" && (
                            <textarea
                                value={formData[currentQ.field] || ""}
                                onChange={(e) => handleInputChange(currentQ.field, e.target.value)}
                                style={styles.textarea}
                                placeholder={currentQ.placeholder || ""}
                                rows={4}
                            />
                        )}

                        {/* UPLOAD */}
                        {currentQ.type === "upload" && (
                            <div style={styles.uploadContainer}>
                                <div style={styles.uploadArea}>
                                    <Upload size={40} color="#FE8235" />
                                    <p style={styles.uploadText}>Click to upload or drag and drop</p>
                                    <p style={styles.uploadSubtext}>PDF, JPG, PNG (max 10MB)</p>
                                    <input
                                        type="file"
                                        multiple
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={handleFileUpload}
                                        style={styles.fileInput}
                                    />
                                </div>
                                {uploadedFiles.length > 0 && (
                                    <div style={styles.fileList}>
                                        {uploadedFiles.map((file, idx) => (
                                            <div key={idx} style={styles.fileItem}>
                                                <CheckCircle size={18} color="#FE8235" />
                                                <span>{file.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* DETAILS when radio=Yes */}
                        {currentQ.detailsField && formData[currentQ.field] === "Yes" && (
                            <textarea
                                value={formData[currentQ.detailsField] || ""}
                                onChange={(e) => handleInputChange(currentQ.detailsField, e.target.value)}
                                style={styles.detailsTextarea}
                                placeholder="Please provide details"
                                rows={3}
                            />
                        )}

                    </div>

                    <button
                        onClick={goNext}
                        style={{ ...styles.nextButton, opacity: validateQuestion() ? 1 : 0.5 }}
                        disabled={!validateQuestion()}
                    >
                        {currentQuestion === totalQuestions - 1 ? "SUBMIT" : "NEXT"} <ArrowRight size={20} />
                    </button>

                    <div style={styles.privacyNote}>
                        <p>
                            *Your data is safe with us. We follow strict security measures to protect your privacy
                            and never share your information without consent. <a href="#" style={styles.link}>Learn more</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ===== Styles (same as Quick/Minimum look) ===== */
const styles = {
    container: { minHeight: "100vh", background: "#F5F5F5", fontFamily: "Inter, system-ui, sans-serif", display: "flex", flexDirection: "column" },
    header: { display: "flex", alignItems: "center", background: "#FE8235", padding: "20px 25px" },
    logoContainer: { display: "flex", alignItems: "center", gap: "14px" },
    textContainer: { display: "flex", flexDirection: "column", justifyContent: "center", lineHeight: "1.2" },
    logoText: { fontSize: "32px", fontWeight: "700", color: "#FFFFFF", margin: 0, letterSpacing: "0.5px" },
    subtitle: { fontSize: "14px", color: "#E0E0E0", margin: 0, marginTop: "4px" },
    mainContent: { flex: 1, display: "flex", flexDirection: "column", padding: "15px", maxWidth: "960px", margin: "0 auto", width: "100%", boxSizing: "border-box" },

    sectionHeader: { display: "flex", alignItems: "center", position: "relative", marginBottom: "15px", height: "30px" },
    backArrow: { position: "absolute", left: 0, cursor: "pointer" },
    sectionCenter: { width: "100%", display: "flex", justifyContent: "center" },
    sectionName: { fontSize: "18px", fontWeight: "600", color: "#FE8235", textAlign: "center" },

    progressContainer: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" },
    progressBar: { flex: 1, height: "8px", background: "#E0E0E0", borderRadius: "20px", overflow: "hidden" },
    progressFill: { height: "100%", background: "#FE8235", transition: "width 0.3s ease", borderRadius: "20px" },
    progressText: { fontSize: "16px", fontWeight: "600", color: "#5A5A5A", minWidth: "45px" },

    redFlagAlert: { marginBottom: "15px", background: "#FEE", border: "2px solid #C33", borderRadius: "8px", padding: "12px", display: "flex", gap: "10px", alignItems: "start", color: "#C33", fontSize: "13px" },

    questionCard: { flex: 0, background: "#FFFFFF", borderRadius: "0", padding: "40px 30px 30px", boxShadow: "none", display: "flex", flexDirection: "column", minHeight: 0 },
    questionTitle: { fontSize: "24px", fontWeight: "600", color: "#3A3A3A", marginBottom: "40px", textAlign: "left", lineHeight: "1.4" },
    answerArea: { flex: 1, overflowY: "auto", minHeight: 0, paddingBottom: "8px" },

    phoneWrapper: { display: "flex", alignItems: "center", borderBottom: "2px solid #FE8235", paddingBottom: "8px", overflow: "hidden", width: "100%", boxSizing: "border-box" },
    phonePrefix: { color: "#3A3A3A", fontWeight: "600", marginRight: "8px", fontSize: "20px", flexShrink: 0 },
    phoneInput: { flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontSize: "20px", color: "#3A3A3A", overflow: "hidden" },

    textInput: { width: "100%", padding: "8px 0", fontSize: "20px", border: "none", borderBottom: "2px solid #FE8235", outline: "none", background: "transparent", boxSizing: "border-box", transition: "border-color 0.2s", color: "#3A3A3A" },
    select: { width: "100%", padding: "8px 0", fontSize: "20px", border: "none", borderBottom: "2px solid #FE8235", outline: "none", background: "transparent", cursor: "pointer", boxSizing: "border-box", color: "#3A3A3A" },

    radioGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '16px',
        cursor: 'pointer',
        padding: '16px 20px',
        borderRadius: '8px',
        transition: 'all 0.2s'
    },
    radioInput: {
        width: '20px',
        height: '20px',
        cursor: 'pointer',
        accentColor: '#FE8235',
        flexShrink: 0
    },
    // ✅ Checkbox area
    checkboxGroup: {
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        marginBottom: "12px",
    },

    checkboxLabel: {
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 16px",
        borderRadius: "10px",
        border: "2px solid #E0E0E0",
        fontSize: "15px",
        cursor: "pointer",
        userSelect: "none",
        transition: "transform .05s ease",
        boxShadow: "0 0 0 0 rgba(0,0,0,0)",         // keeps layout stable
    },

    checkboxInput: {
        width: "18px",
        height: "18px",
        accentColor: "#FE8235",
        cursor: "pointer",
        flexShrink: 0,
    },

    // ✅ “Other” field under the checkboxes
    otherInput: {
        width: "100%",
        padding: "10px 0",
        fontSize: "16px",
        border: "none",
        borderBottom: "2px solid #FE8235",
        outline: "none",
        background: "transparent",
        boxSizing: "border-box",
        color: "#3A3A3A",
    },

    // Textarea (matches your input aesthetic)
    textarea: {
        width: "100%",
        padding: "12px 14px",
        fontSize: "16px",
        lineHeight: 1.5,
        color: "#3A3A3A",
        background: "#FFFFFF",
        border: "2px solid #E0E0E0",
        borderRadius: "10px",
        outline: "none",
        resize: "vertical",      // allow user to resize if they want
        minHeight: "96px",
        boxSizing: "border-box",
        transition: "border-color .15s ease",
    },
    detailsTextarea: {
        marginTop: "16px",
        width: "100%",
        padding: "12px 14px",
        fontSize: "16px",
        lineHeight: 1.5,
        color: "#3A3A3A",
        background: "#FFFFFF",
        border: "2px solid #E0E0E0",
        borderRadius: "10px",
        outline: "none",
        resize: "vertical",
        minHeight: "96px",
        boxSizing: "border-box",
        transition: "border-color .15s ease",
    },


    // Optional focus/hover helpers (use inline if you prefer)
    textareaFocus: {
        borderColor: "#FE8235",
    },


    diagnosticsCard: {
        background: "#FFFFFF",
        borderRadius: "12px",
        padding: "40px 30px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        margin: "40px auto",
        maxWidth: "600px",
        textAlign: "left",
    },
    sectionTitle: {
        fontSize: "22px",
        fontWeight: "700",
        color: "#FE8235",
        marginBottom: "20px",
    },
    questionTitle: {
        fontSize: "18px",
        fontWeight: "600",
        color: "#333",
        marginTop: "20px",
        marginBottom: "10px",
    },

    sliderContainer: { textAlign: "center" },
    sliderValue: { fontSize: "36px", fontWeight: "700", color: "#FE8235", marginBottom: "15px" },
    slider: { width: "100%", height: "6px", borderRadius: "10px", outline: "none", accentColor: "#FE8235", cursor: "pointer" },
    sliderLabels: { display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#888", marginTop: "8px" },

    uploadContainer: { maxWidth: "100%", margin: "0 auto" },
    uploadArea: { border: "2px dashed #E0E0E0", borderRadius: "8px", padding: "30px 15px", textAlign: "center", cursor: "pointer", position: "relative", transition: "all 0.2s", background: "#FAFAFA" },
    uploadText: { margin: "12px 0 4px", fontSize: "14px", fontWeight: "600", color: "#3A3A3A" },
    uploadSubtext: { margin: 0, fontSize: "12px", color: "#888" },
    fileInput: { position: "absolute", inset: 0, opacity: 0, cursor: "pointer" },
    fileList: { marginTop: "10px", display: "flex", flexDirection: "column", gap: "8px", maxHeight: "120px", overflowY: "auto" },
    fileItem: { display: "flex", alignItems: "center", gap: "8px", padding: "10px", background: "#FFF4ED", borderRadius: "6px", fontSize: "13px" },

    nextButton: { width: "100%", padding: "10px", fontSize: "16px", fontWeight: "700", color: "#FFFFFF", background: "#FE8235", border: "none", borderRadius: "8px", cursor: "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", marginTop: "20px", marginBottom: "16px", flexShrink: 0, letterSpacing: "1px" },
    privacyNote: { textAlign: "center", fontSize: "11px", color: "#999", lineHeight: "1.6", flexShrink: 0 },
    link: { color: "#999", textDecoration: "underline" },

    thankYouCard: { maxWidth: "600px", margin: "80px auto", background: "#FFFFFF", borderRadius: "12px", padding: "50px 30px", textAlign: "center", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" },
    thankYouTitle: { fontSize: "28px", fontWeight: "700", color: "#3A3A3A", margin: "0 0 16px 0" },
    thankYouText: { fontSize: "16px", color: "#666", lineHeight: "1.6", marginBottom: "40px" }
};
